import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  ShoppingCart,
  CheckCircle,
  Truck,
  Shield,
  Users,
  Package,
  Sparkles,
  ImageIcon,
  Star,
  ChevronRight,
  Search,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  getProgramDetails,
  getProgramDefaultMedications,
  type ProgramDetailsResponse,
  type DefaultMedication,
} from '@/services/storefrontService';
import { toast } from '@/hooks/use-toast';
import { useClinic } from '@/contexts/ClinicContext';
import { useCart, type CartItem as CartContextItem } from '@/contexts/CartContext';
import { permissionCheck } from '@/lib/permission-check';
import { Skeleton } from '@/components/ui/skeleton';
import MedicationDrawer from '@/components/shared/MedicationDrawer';
import ProtocolOptionCard, { type ProgramOption } from '@/components/storefront/ProtocolOptionCard';
import { MedicationSelection } from '@/components/shared/medication-types';
import { useBreadcrumbLabel } from '@/components/ui/breadcrumb';

const ProgramDetail: React.FC = () => {
  const { programId } = useParams<{ programId: string }>();
  const { clinic, isClinicStaff, isAdmin, rolePermissions, isPractitioner } =
    useClinic();
  const { addToCart, removeFromCart, cartCount, items: cartItems, selectedPatient } = useCart();
  const navigate = useNavigate();
  const [program, setProgram] = useState<ProgramDetailsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [defaultMedicationIds, setDefaultMedicationIds] = useState<Set<string>>(new Set());
  const [defaultMedications, setDefaultMedications] = useState<DefaultMedication[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedDrawerMedication, setSelectedDrawerMedication] = useState<any | null>(null);
  const [medicationSearch, setMedicationSearch] = useState('');
  const [selectedOption, setSelectedOption] = useState<ProgramOption | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const { setLabel: setBreadcrumbLabel } = useBreadcrumbLabel();

  // Show program title in breadcrumb instead of ID
  useEffect(() => {
    if (program?.title) setBreadcrumbLabel(program.title);
    return () => setBreadcrumbLabel(null);
  }, [program?.title, setBreadcrumbLabel]);

  useEffect(() => {
    if (!programId) return;

    setMedicationSearch('');
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const signal = controller.signal;

    const fetchProgramDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        const programData = await getProgramDetails(programId!, clinic?.id, signal, selectedPatient?.id);
        if (signal.aborted) return;

        // Flatten medications into product-level entries (one per medication+dose)
        const flattenedMedications: any[] = [];
        for (const med of (programData.medications || [])) {
          const doses = (med.doses || []).filter((d: string) => d && d.trim() !== '');
          if (doses.length === 0) continue;
          for (const dose of doses) {
            const product = med.map_dose_to_product?.find((p: any) => p.dose === dose);
            flattenedMedications.push({
              ...med,
              id: `${med.id}::${dose}`,
              originalMedId: med.id,
              name: med.name,
              dose,
              doses: [dose],
              product_id: product?.product_id || null,
              price: parseFloat(product?.price || '0'),
              base_price: parseFloat(product?.base_price || '0'),
              image_url: product?.image_url || med.image_url || null,
              map_dose_to_product: product ? [product] : [],
            });
          }
        }
        programData.medications = flattenedMedications;
        setProgram(programData);

        try {
          const defaultMeds = await getProgramDefaultMedications(programId!, clinic?.id, signal, selectedPatient?.id);
          if (signal.aborted) return;
          if (defaultMeds && defaultMeds.length > 0) {
            setDefaultMedications(defaultMeds);
            // Build default IDs matching flattened product keys (originalMedId::dose)
            const defaultIds = defaultMeds.map((dm: DefaultMedication) => `${dm.medication_id}::${dm.dose}`);
            setDefaultMedicationIds(new Set(defaultIds));
          } else {
            setDefaultMedications([]);
          }
        } catch (defaultsErr) {
          if (defaultsErr instanceof Error && defaultsErr.name === 'AbortError') return;
          console.warn('Could not load default medications:', defaultsErr);
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        console.error('Error fetching program details:', err);
        if (!signal.aborted) setError('Failed to load program details. Please try again.');
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    };

    fetchProgramDetails();
    return () => {
      controller.abort();
      abortRef.current = null;
    };
  }, [programId, clinic?.id, selectedPatient?.id]);

  const handleMedicationClick = (medication: any) => {
    setSelectedDrawerMedication(medication);
    setDrawerOpen(true);
  };

  const handleAddToCartFromDrawer = (selection: MedicationSelection) => {
    // Check if medication already exists in cart (by medication_id)
    const existingCartItem = cartItems.find(
      item => item.medication_id === selectedDrawerMedication.id
    );

    // If it exists and the product_id is different (dose changed), remove the old one first
    if (existingCartItem && existingCartItem.product_id !== selection.product_id) {
      removeFromCart(existingCartItem.product_id);
    }

    const cartItem: CartContextItem = {
      program_id: program!.id,
      program_name: program!.title,
      subtitle: program!.subtitle || '',
      medication_name: selectedDrawerMedication.name,
      dose: selection.dose,
      product_id: selection.product_id,
      quantity: selection.quantity || 1,
      price: selection.price,
      base_price: selection.base_price,
      discount: 0,
      unit_of_measure: selection.unit_of_measure,
      medication_id: selectedDrawerMedication.id,
      sig: selection.sig,
      reason: selection.reason,
      daysSupply: selection.daysSupply,
      refills: selection.refills_authorized ?? '0',
      template_id: selectedDrawerMedication.template_id,
      product_type: selection.product_type || null,
      display_mode: program?.display_mode || 'medication_select',
    };

    addToCart(cartItem);

    toast({
      title: existingCartItem ? 'Cart updated' : 'Added to cart',
      description: existingCartItem
        ? `${selectedDrawerMedication.name} updated in cart successfully.`
        : `${selectedDrawerMedication.name} added to cart successfully.`,
      variant: 'success',
    });
  };

  const isMedicationInCart = (medicationId: string): boolean => {
    return cartItems.some(item => item.medication_id === medicationId);
  };

  const getCartItemAsSelection = (medicationId: string): MedicationSelection | null => {
    const cartItem = cartItems.find(item => item.medication_id === medicationId);
    if (!cartItem) return null;

    return {
      quantity: cartItem.quantity,
      dose: cartItem.dose,
      sig: cartItem.sig || '',
      product_id: cartItem.product_id,
      price: cartItem.price,
      base_price: cartItem.base_price,
      program_name: cartItem.program_name,
      program_id: cartItem.program_id,
      medication_name: cartItem.medication_name,
      daysSupply: cartItem.daysSupply || '30',
      reason: cartItem.reason || '',
      unit_of_measure: cartItem.unit_of_measure || '',
      refills_authorized: cartItem.refills || '0',
      template_id: cartItem.template_id || null,
    };
  };

  const getDefaultMedicationAsSelection = (
    medication: any,
    defaultMed: DefaultMedication
  ): MedicationSelection | null => {
    if (!program || medication.id !== defaultMed.medication_id) return null;
    const product = medication.map_dose_to_product?.find(
      (p: any) => p.dose === defaultMed.dose
    );
    const defaultReason = `${program.title} - ${program.subtitle || 'Order program'}`;
    return {
      quantity: defaultMed.quantity ?? 1,
      dose: defaultMed.dose,
      sig: defaultMed.sig || '',
      product_id: defaultMed.product_id || product?.product_id || '',
      price: parseFloat(product?.price || '0'),
      base_price: parseFloat(product?.base_price || '0'),
      program_name: program.title,
      program_id: program.id,
      medication_name: medication.name,
      daysSupply: defaultMed.days_supply || '30',
      reason: product?.compounding_reason || defaultReason,
      unit_of_measure: medication?.unit_of_measure || defaultMed.unit_of_measure || '',
      refills_authorized: String(defaultMed.refills_authorized ?? 0),
      template_id: medication.template_id || null,
    };
  };

  const getDrawerExistingSelection = (medication: any): MedicationSelection | null => {
    const cartSelection = getCartItemAsSelection(medication.id);
    if (cartSelection) return cartSelection;
    const defaultMed = defaultMedications.find(dm => dm.medication_id === medication.id);
    if (defaultMed) return getDefaultMedicationAsSelection(medication, defaultMed);
    return null;
  };

  const handleNavigation = (url: string) => {
    if (isPractitioner) {
      navigate(`/practitioner-portal${url}`);
    } else {
      // Admin + clinic_staff both live at root.
      navigate(url);
    }
  };

  const canEdit = permissionCheck(
    'storefront:update',
    rolePermissions,
    isAdmin || isPractitioner || false
  );
  const canCreate = permissionCheck(
    'storefront:create',
    rolePermissions,
    isAdmin || isPractitioner || false
  );
  const canDoAction = canEdit || canCreate;

  // Benefits data
  const benefits = [
    {
      icon: CheckCircle,
      title: 'Comprehensive Care',
      description:
        'Complete treatment approach with all necessary medications for optimal results.',
    },
    {
      icon: Truck,
      title: 'Fast Delivery',
      description: 'Medications delivered directly to your door with flexible scheduling.',
    },
    {
      icon: Shield,
      title: 'Quality Assured',
      description: 'FDA-approved medications from reputable pharmaceutical manufacturers.',
    },
    {
      icon: Users,
      title: 'Expert Support',
      description: 'Access to healthcare professionals throughout your treatment journey.',
    },
  ];

  if (loading) {
    return (
      <div className='space-y-8'>
        {/* Hero Skeleton */}
        <div className='relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-100 to-slate-50'>
          <div className='flex flex-col lg:flex-row'>
            <div className='flex-1 p-8 lg:p-12'>
              <Skeleton className='mb-4 h-8 w-40' />
              <Skeleton className='mb-3 h-12 w-3/4' />
              <Skeleton className='mb-6 h-6 w-1/2' />
              <Skeleton className='mb-8 h-20 w-full' />
              <div className='flex gap-4'>
                <Skeleton className='h-14 w-44' />
                <Skeleton className='h-14 w-36' />
              </div>
            </div>
            <div className='p-8 lg:p-12'>
              <Skeleton className='h-72 w-full rounded-2xl lg:w-96' />
            </div>
          </div>
        </div>

        {/* Benefits Skeleton */}
        <div className='grid grid-cols-2 gap-4 lg:grid-cols-4'>
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className='h-32 rounded-2xl' />
          ))}
        </div>

        {/* Products Skeleton */}
        <div>
          <Skeleton className='mb-6 h-10 w-64' />
          <div className='grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'>
            {[1, 2, 3, 4].map(i => (
              <Skeleton key={i} className='h-[420px] rounded-2xl' />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !program) {
    return (
      <div className='flex min-h-[500px] items-center justify-center'>
        <div className='text-center'>
          <div className='mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-slate-100'>
            <Package className='h-10 w-10 text-slate-400' />
          </div>
          <h1 className='mb-3 text-2xl font-bold text-slate-900'>
            {error ? 'Error Loading Program' : 'Program Not Found'}
          </h1>
          <p className='mb-8 max-w-md text-slate-500'>
            {error || "The program you're looking for doesn't exist or has been removed."}
          </p>
          <Button
            onClick={() => handleNavigation('/storefront')}
            variant='outline'
            className='h-12 rounded-full px-6'
          >
            <ArrowLeft className='mr-2 h-4 w-4' />
            Back to Programs
          </Button>
        </div>
      </div>
    );
  }

  // Filter valid medications
  const medications = (
    program?.medications?.filter(
      medication => medication.name && medication.doses && medication.doses.length > 0
    ) || []
  ).sort((a, b) => {
    const aIsDefault = defaultMedicationIds.has(a.id) ? 1 : 0;
    const bIsDefault = defaultMedicationIds.has(b.id) ? 1 : 0;
    return bIsDefault - aIsDefault;
  });
  const normalizedSearch = medicationSearch.trim().toLowerCase();
  const filteredMedications = medications.filter(medication => {
    if (!normalizedSearch) return true;

    const doseText = Array.isArray(medication.doses)
      ? medication.doses.join(' ')
      : String(medication.doses || '');
    const searchableText = `${medication.name || ''} ${doseText}`.toLowerCase();
    return searchableText.includes(normalizedSearch);
  });

  // For guided clinics, treat null/missing display_mode as protocol_showcase (guided-only programs).
  const isProtocolShowcase =
    program?.display_mode === 'protocol_showcase';
  const programOptions: ProgramOption[] = (program?.program_options || [])
    .sort((a: ProgramOption, b: ProgramOption) => a.sort_order - b.sort_order);


  const protocolCtaLabel = isProtocolShowcase ? 'Select' : 'Get Started';

  const handleSelectOption = (option: ProgramOption) => {
    setSelectedOption(prev => prev?.id === option.id ? null : option);
  };

  const handleAddOptionToCart = () => {
    if (!selectedOption || !program) return;
    const cartItem: CartContextItem = {
      medication_id: selectedOption.id,
      medication_name: selectedOption.label,
      subtitle: program.subtitle || '',
      dose: '',
      sig: '',
      quantity: 1,
      price: selectedOption.program_price / 100,
      base_price: selectedOption.program_price / 100,
      discount: 0,
      program_name: program.title,
      program_id: programId || '',
      product_id: selectedOption.id,
      unit_of_measure: 'program',
      daysSupply: selectedOption.is_subscription
        ? (selectedOption.subscription_frequency === 'monthly' ? '30' : selectedOption.subscription_frequency === 'quarterly' ? '90' : '7')
        : '30',
      clinic_program_option_id: selectedOption.id,
      display_mode: program.display_mode || 'medication_select',
    };
    addToCart(cartItem);
    toast({
      title: 'Added to cart',
      description: `${selectedOption.label} added to cart.`,
    });
  };

  return (
    <div className='space-y-10 pb-12'>
      {/* Hero Section - Modern E-commerce Style */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className='relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'
      >
        {/* Background Pattern */}
        <div className='absolute inset-0 opacity-20'>
          <div
            className='absolute inset-0'
            style={{
              backgroundImage:
                'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 1px, transparent 0)',
              backgroundSize: '32px 32px',
            }}
          />
        </div>

        {/* Decorative Gradient Orbs */}
        <div className='absolute -right-32 -top-32 h-96 w-96 rounded-full bg-gradient-to-br from-emerald-500/30 to-teal-500/20 blur-3xl' />
        <div className='absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-gradient-to-tr from-violet-500/20 to-purple-500/10 blur-3xl' />

        <div className='relative'>
          <div className='flex flex-col lg:flex-row'>
            {/* Content - Left Side */}
            <div className='flex flex-1 flex-col justify-center p-6 lg:p-10 xl:p-12'>
              {/* Back & Badge Row */}
              <div className='mb-4 flex items-center gap-3'>
                <button
                  onClick={() => handleNavigation('/storefront')}
                  className='flex h-8 w-8 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white backdrop-blur-sm transition-all hover:bg-white/20'
                >
                  <ArrowLeft className='h-4 w-4' />
                </button>
                <div className='inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 px-3 py-1.5 text-xs font-semibold text-white shadow-lg shadow-emerald-500/25'>
                  <Sparkles className='h-3.5 w-3.5' />
                  Health Program
                </div>
              </div>

              <h1
                className='mb-3 font-montserrat font-bold tracking-tight text-white'
                style={{ fontSize: '2.5rem' }}
              >
                {program.title}
              </h1>
              <p className='mb-3 text-base font-medium text-emerald-400 lg:text-lg'>
                {program.subtitle}
              </p>

              <p className='mb-6 max-w-2xl text-sm leading-relaxed text-slate-300 lg:text-base'>
                {program.description}
              </p>

              {/* Target Audience */}
              {program.target && (
                <div className='mb-6 inline-flex max-w-xl items-start gap-2.5 rounded-xl border border-white/10 bg-white/5 p-3 backdrop-blur-sm'>
                  <div className='flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-emerald-500/20'>
                    <Star className='h-4 w-4 text-emerald-400' />
                  </div>
                  <div>
                    <p className='text-[10px] font-semibold uppercase tracking-wider text-emerald-400'>
                      Ideal For
                    </p>
                    <p className='mt-0.5 text-xs leading-relaxed text-slate-300'>
                      {program.target}
                    </p>
                  </div>
                </div>
              )}

              {/* CTA Buttons */}
              <div className='flex flex-col gap-3 sm:flex-row'>
                <Button
                  onClick={() => handleNavigation('/storefront/cart')}
                  className='h-11 gap-2 rounded-full bg-white px-6 text-sm font-semibold text-slate-900 shadow-xl shadow-white/25 transition-all hover:bg-slate-100 hover:shadow-2xl'
                >
                  <ShoppingCart className='h-4 w-4' />
                  View Cart ({cartCount})
                </Button>
                {!isProtocolShowcase && (
                  <Button
                    variant='outline'
                    onClick={() => {
                      const productsSection = document.getElementById('products-section');
                      productsSection?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className='h-11 gap-2 rounded-full border-white/30 bg-transparent px-6 text-sm font-semibold text-white hover:bg-white/10 hover:text-white'
                  >
                    Browse Products
                    <ChevronRight className='h-4 w-4' />
                  </Button>
                )}
              </div>
            </div>

            {/* Program Image - Right Side */}
            <div className='relative flex w-full items-center justify-center p-6 lg:w-auto lg:p-10 xl:p-12'>
              {program.image_url ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className='group relative'
                >
                  <div className='absolute -inset-4 rounded-3xl bg-gradient-to-br from-emerald-500/30 to-teal-500/30 opacity-75 blur-2xl transition-all duration-500 group-hover:opacity-100 group-hover:blur-3xl' />
                  <div className='relative h-48 w-full min-w-[280px] overflow-hidden rounded-3xl border-2 border-white/20 shadow-2xl sm:h-64 sm:min-w-[400px] lg:h-80 lg:w-[520px]'>
                    <img
                      src={program.image_url}
                      alt={program.title}
                      className='h-full w-full object-cover transition-transform duration-700 group-hover:scale-105'
                    />
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className='relative'
                >
                  <div className='absolute -inset-4 rounded-3xl bg-gradient-to-br from-slate-600/30 to-slate-700/30 blur-2xl' />
                  <div className='relative flex h-48 w-full min-w-[280px] items-center justify-center overflow-hidden rounded-3xl border-2 border-white/10 bg-gradient-to-br from-slate-700 to-slate-800 sm:h-64 sm:min-w-[400px] lg:h-80 lg:w-[520px]'>
                    <div className='text-center'>
                      <div className='mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-2xl border border-white/20 bg-white/10'>
                        <ImageIcon className='h-10 w-10 text-white/50' strokeWidth={1.5} />
                      </div>
                      <p className='text-sm text-white/40'>No image available</p>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Benefits Strip */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className='grid grid-cols-2 gap-4 lg:grid-cols-4'
      >
        {benefits.map((benefit, index) => (
          <motion.div
            key={benefit.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 + index * 0.05 }}
            className='group relative overflow-hidden rounded-xl border border-slate-200 bg-white p-4 transition-all duration-300 hover:border-emerald-200 hover:shadow-lg hover:shadow-emerald-100/50'
          >
            <div className='absolute -right-4 -top-4 h-16 w-16 rounded-full bg-gradient-to-br from-emerald-100 to-teal-100 opacity-0 transition-opacity duration-300 group-hover:opacity-100' />
            <div className='relative'>
              <div className='mb-2.5 flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 shadow-lg shadow-emerald-500/25'>
                <benefit.icon className='h-5 w-5 text-white' />
              </div>
              <h3 className='mb-0.5 text-sm font-semibold text-slate-900'>{benefit.title}</h3>
              <p className='text-xs leading-relaxed text-slate-500'>{benefit.description}</p>
            </div>
          </motion.div>
        ))}
      </motion.div>

{console.log('isProtocolShowcase', isProtocolShowcase)}
      {/* Protocol Showcase Mode (Guided Clinics) */}
      {isProtocolShowcase && programOptions.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className='mb-6'>
            <h2 className='font-montserrat text-xl font-bold tracking-tight text-slate-900 sm:text-2xl'>
              Choose Your Program
            </h2>
            <p className='mt-1 text-sm text-slate-500'>
              Select the option that best fits your needs. Consulting fees cover your clinical program.
            </p>
          </div>

          <div className='grid gap-4 md:grid-cols-2'>
            {programOptions.map(option => (
              <ProtocolOptionCard
                key={option.id}
                option={option}
                programTitle={program?.title || ''}
                programId={programId || ''}
                isSelected={selectedOption?.id === option.id}
                onSelect={handleSelectOption}
                ctaLabel={protocolCtaLabel}
              />
            ))}
          </div>

          {selectedOption && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className='mt-6 flex justify-center'
            >
              <Button
                size='lg'
                className='px-12'
                onClick={handleAddOptionToCart}
              >
                <ShoppingCart className='mr-2 h-5 w-5' />
                Add to Cart — ${(selectedOption.program_price / 100).toFixed(2)}
              </Button>
            </motion.div>
          )}
        </motion.div>
      )}

      {/* Products Section (medication_select mode)
           NOTE: This entire block (medication grid + drawer) is intentionally hidden when the
           program uses protocol_showcase (guided clinic). In that mode patients and staff must
           only see program option cards above — never individual medication names, doses, or
           medication-level pricing. Do NOT move this guard without a corresponding update to the
           protocol_showcase option grid above. */}
      {!isProtocolShowcase && (<>
        <motion.div
          id='products-section'
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          {/* Section Header */}
          <div className='mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between'>
            <div>
              <h2 className='font-montserrat text-xl font-bold tracking-tight text-slate-900 sm:text-2xl'>
                Shop Medications
              </h2>
              <p className='mt-1 text-sm text-slate-500'>
                Select from {filteredMedications.length}
                {normalizedSearch ? ` of ${medications.length}` : ''} available products in this program
              </p>
            </div>
            <div className='relative w-full sm:max-w-xs'>
              <Search className='pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400' />
              <Input
                value={medicationSearch}
                onChange={e => setMedicationSearch(e.target.value)}
                placeholder='Search medications...'
                className='pl-9'
              />
            </div>
          </div>

          {/* Products Grid - Shopify Style */}
          {filteredMedications.length === 0 ? (
            <div className='flex flex-col items-center justify-center rounded-3xl border-2 border-dashed border-slate-200 bg-slate-50 py-20'>
              <div className='mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-100'>
                <Package className='h-8 w-8 text-slate-400' />
              </div>
              <h3 className='mb-2 text-lg font-semibold text-slate-700'>
                {normalizedSearch ? 'No Matching Medications' : 'No Products Available'}
              </h3>
              <p className='text-slate-500'>
                {normalizedSearch
                  ? 'Try a different search term.'
                  : 'Products will appear here once they are added.'}
              </p>
            </div>
          ) : (
            <div className='grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'>
              {filteredMedications.map((medication, index) => {
                const isInCart = isMedicationInCart(medication.id);
                const isDefault = defaultMedicationIds.has(medication.id);
                const productIndex = medication.map_dose_to_product?.findIndex(
                  product => product.price > 0
                );
                const displayPrice = parseFloat(medication.map_dose_to_product?.[productIndex]?.price?.toString?.() || '0');
                const availableDoses =
                  medication.doses?.filter((dose: string) => dose && dose.trim() !== '') || [];

                return (
                  <motion.div
                    key={medication.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.05 * index }}
                    onClick={() => handleMedicationClick(medication)}
                    className='group cursor-pointer'
                  >
                    <div
                      className={`relative h-full overflow-hidden rounded-xl border bg-white transition-all duration-300 ${isInCart
                          ? 'border-emerald-400 shadow-lg shadow-emerald-100'
                          : 'border-slate-200 hover:border-slate-300 hover:shadow-xl'
                        }`}
                    >
                      {/* Product Image Area */}
                      <div className='relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-slate-100 to-slate-50'>
                        {medication.image_url ? (
                          <img
                            src={medication.image_url}
                            alt={medication.name}
                            className='h-full w-full object-contain transition-transform duration-500 group-hover:scale-110'
                          />
                        ) : (
                          <div className='flex h-full w-full items-center justify-center'>
                            <div className='text-center'>
                              <svg
                                className='h-20 w-20 text-slate-300 transition-transform duration-300 group-hover:scale-110'
                                viewBox='0 0 40 40'
                                fill='currentColor'
                              >
                                <path d='M5.667,31.843C5.3,31.843,5,31.544,5,31.175V8.823c0-0.367,0.3-0.666,0.667-0.666h28.666C34.7,8.157,35,8.456,35,8.823 v22.352c0,0.369-0.3,0.668-0.667,0.668H5.667z M7.044,30.507h25.734l-8.068-8.582l-3.695,3.928 c-0.127,0.135-0.303,0.207-0.494,0.207c-0.225-0.008-0.404-0.105-0.524-0.262l-4.704-6.205L7.044,30.507z M24.71,20.282 c0.182,0,0.359,0.076,0.485,0.209l8.469,9.008V9.493H6.336v19.734l8.421-11.143c0.127-0.166,0.326-0.268,0.533-0.268 c0.213,0,0.406,0.098,0.533,0.266l4.759,6.275l3.639-3.867C24.349,20.358,24.526,20.282,24.71,20.282L24.71,20.282z' />
                                <path d='M25.154,17.769c-1.616,0-2.931-1.316-2.931-2.932s1.314-2.93,2.931-2.93c1.617,0,2.932,1.314,2.932,2.93 S26.771,17.769,25.154,17.769L25.154,17.769z M25.154,13.243c-0.879,0-1.595,0.715-1.595,1.594s0.716,1.596,1.595,1.596 c0.88,0,1.596-0.717,1.596-1.596S26.034,13.243,25.154,13.243L25.154,13.243z' />
                              </svg>
                            </div>
                          </div>
                        )}

                        {/* Badges Overlay */}
                        {isDefault && !isInCart && (
                          <div className='absolute left-3 top-3'>
                            <Badge className='bg-amber-500 px-3 py-1 text-xs font-semibold text-white shadow-lg'>
                              Recommended
                            </Badge>
                          </div>
                        )}
                        {isInCart && (
                          <div className='absolute right-3 top-3'>
                            <Badge className='bg-emerald-500 px-3 py-1 text-xs font-semibold text-white shadow-lg'>
                              <ShoppingCart className='mr-1.5 h-3 w-3' />
                              In Cart
                            </Badge>
                          </div>
                        )}


                        {/* Quick Add Overlay */}
                        <div className='absolute inset-x-0 bottom-0 translate-y-full bg-gradient-to-t from-black/80 to-transparent p-4 transition-transform duration-300 group-hover:translate-y-0'>
                          <Button className='w-full rounded-xl bg-white py-3 font-semibold text-slate-900 shadow-xl hover:bg-slate-100'>
                            {isInCart ? 'Edit Selection' : 'Configure & Add'}
                          </Button>
                        </div>
                      </div>

                      {/* Product Info */}
                      <div className='p-4'>
                        <h3 className='mb-2 line-clamp-2 text-sm font-semibold leading-tight text-slate-900 transition-colors group-hover:text-emerald-600'>
                          {medication.name}
                        </h3>

                        {/* Variants Info */}
                        {availableDoses.length > 1 && (
                          <div className='mb-3 flex flex-wrap gap-2'>
                            <span className='inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-medium text-slate-600'>
                              {availableDoses.length} doses available
                            </span>
                          </div>
                        )}

                        {/* Price */}
                        <div className='flex items-end justify-between'>
                          <div>
                            <p className='text-[10px] font-medium uppercase tracking-wider text-slate-400'>
                              {availableDoses.length === 1 ? 'Starting At' : 'Starting At'}
                            </p>
                            <p className='text-lg font-bold text-slate-900'>
                              ${displayPrice > 0 ? displayPrice.toFixed(2) : '0.00'}
                            </p>
                          </div>
                          <div className='flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500 text-white opacity-0 shadow-lg transition-all duration-300 group-hover:opacity-100'>
                            <ChevronRight className='h-4 w-4' />
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </motion.div>

        {/* Medication Drawer */}
        {program && (
          <MedicationDrawer
            open={drawerOpen}
            onOpenChange={setDrawerOpen}
            medication={selectedDrawerMedication}
            programData={program}
            onSubmit={handleAddToCartFromDrawer}
            submitButtonText='Add to Cart'
            mode='cart'
            existingSelection={
              selectedDrawerMedication ? getDrawerExistingSelection(selectedDrawerMedication) : null
            }
          />
        )}
      </>)}

      {/* Floating Cart Button - Mobile */}
      {cartCount > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          className='fixed bottom-6 left-1/2 z-50 -translate-x-1/2 lg:hidden'
        >
          <Button
            onClick={() => handleNavigation('/storefront/cart')}
            className='h-14 gap-3 rounded-full bg-slate-900 px-8 text-base font-semibold text-white shadow-2xl shadow-slate-900/50 hover:bg-slate-800'
          >
            <ShoppingCart className='h-5 w-5' />
            View Cart ({cartCount})
          </Button>
        </motion.div>
      )}
    </div>
  );
};

export default ProgramDetail;
